import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useListTrucksQuery } from "~/shared/api/truck/truck.grpc";
import { humanTruckType } from "~/pages/dashboard/autopark/helpers";
import { compareIds, entityNumber, numberColumn } from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A truck type with the number of the organization's trucks of it, or the exports' totals line. */
interface TruckRegistryRow {
  /** Null for trucks without a truck type, grouped by their label instead. */
  truckTypeId: bigint | null;
  truckType: string;
  count: number;
  isTotal?: boolean;
}

const rowKey = (row: TruckRegistryRow) =>
  row.truckTypeId ? String(row.truckTypeId) : `label:${row.truckType}`;

/** «Сводная информация по реестру ТС» (AIS 15.3.4): how many trucks of each type the fleet has. */
export const TruckRegistryReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { activeCompanyId } = useAuthSession();
    // The fleet as it stands: the report period does not narrow it.
    const { data, isLoading, isFetching, error } = useListTrucksQuery(
      { ownerOrganizationIds: [BigInt(activeCompanyId ?? 0)] },
      { skip: !activeCompanyId },
    );

    const rows = useMemo<TruckRegistryRow[]>(() => {
      const byType = new Map<string, TruckRegistryRow>();
      for (const truck of data?.trucks ?? []) {
        const row: TruckRegistryRow = {
          truckTypeId: truck.truckType?.id || null,
          // The type's own name; the base-type label only for trucks without a type.
          truckType:
            truck.truckType?.name || humanTruckType(truck.truckType?.type ?? truck.type, t),
          count: 0,
        };
        const key = rowKey(row);
        const group = byType.get(key) ?? row;
        group.count += 1;
        byType.set(key, group);
      }
      return [...byType.values()].sort(
        (a, b) =>
          a.truckType.localeCompare(b.truckType) ||
          compareIds(a.truckTypeId ?? 0n, b.truckTypeId ?? 0n),
      );
    }, [data, t]);

    const columns: Column<TruckRegistryRow>[] = useMemo(
      () => [
        numberColumn(
          t(Namespace.Analytics, "columns.number"),
          t(Namespace.Analytics, "collection.totals.label"),
        ),
        {
          id: "id",
          header: t(Namespace.Analytics, "columns.id"),
          accessor: (row) => (row.isTotal ? "" : entityNumber("truckType", row.truckTypeId)),
          width: 110,
          sortable: true,
          sortFn: (a, b) => compareIds(a.truckTypeId ?? 0n, b.truckTypeId ?? 0n),
        },
        {
          id: "truckType",
          header: t(Namespace.Analytics, "equipment.truckType"),
          key: "truckType",
          width: 240,
          sortable: true,
        },
        {
          id: "count",
          header: t(Namespace.Analytics, "truckRegistry.count"),
          key: "count",
          width: 140,
          sortable: true,
          sortFn: (a, b) => a.count - b.count,
        },
      ],
      [t],
    );

    const exportRows = useMemo<TruckRegistryRow[]>(
      () =>
        rows.length
          ? [
              ...rows,
              {
                isTotal: true,
                truckTypeId: null,
                truckType: "",
                count: rows.reduce((sum, r) => sum + r.count, 0),
              },
            ]
          : rows,
      [rows],
    );

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="truck_registry_report"
        reportName={t(Namespace.Analytics, "reports.truckRegistry")}
        columns={columns}
        rows={rows}
        exportRows={exportRows}
        rowKey={rowKey}
        isLoading={isLoading || isFetching}
        error={error}
      />
    );
  },
);
